import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { FLAGS } from "@/components/Flags";
import { useAuth } from "@/context/AuthContext";
import { useI18n, LOCALES } from "@/i18n";

export const Logo = ({ className = "", dark = false }) => (
  <Link to="/" data-testid="logo-link" className={`inline-flex items-baseline gap-2 ${className}`}>
    <span className="font-display text-2xl tracking-[0.18em] font-medium">DOCTHAIR</span>
    <span className={`hidden sm:inline overline text-[9px] ${dark ? "!text-zinc-500" : ""}`}>
      Hair Science
    </span>
  </Link>
);

const NAV_KEYS = [
  ["/how-it-works", "nav.how"],
  ["/science", "nav.science"],
  ["/analysis", "nav.analysis"],
  ["/kit", "nav.kit"],
  ["/hair-health-score", "nav.score"],
  ["/pricing", "nav.pricing"],
  ["/faq", "nav.faq"],
];

export const LocaleSwitch = ({ compact = false }) => {
  const { locale, setLocale } = useI18n();
  return (
    <div className="flex items-center gap-1.5" data-testid="locale-switch">
      {LOCALES.map((l) => {
        const Flag = FLAGS[l.code];
        const active = locale === l.code;
        return (
          <button
            key={l.code}
            data-testid={`locale-${l.code}`}
            onClick={() => setLocale(l.code)}
            title={l.name}
            aria-label={l.name}
            aria-pressed={active}
            className={`flex items-center gap-1.5 rounded-sm px-1.5 py-1 transition-opacity ${
              active ? "opacity-100" : "opacity-40 hover:opacity-80"
            }`}
          >
            <Flag className="h-[11px] w-[17px] shrink-0" />
            {!compact && (
              <span className={`font-mono-dt text-[10px] ${active ? "text-black" : "text-zinc-500"}`}>
                {l.label}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
};

const Header = () => {
  const [open, setOpen] = useState(false);
  const { user, logout, homeFor } = useAuth();
  const { t } = useI18n();
  const { pathname } = useLocation();
  const primaryPath = user ? "/order" : "/diagnostic-gratuit";
  const primaryLabel = user ? t("cta.analyse") : t("cta.freeDiagnostic");

  return (
    <header className="sticky top-0 z-50 border-b border-black/10 bg-white/75 backdrop-blur-xl">
      <div className="mx-auto flex h-[72px] max-w-[1400px] items-center justify-between gap-6 px-5 md:px-10">
        <Logo />
        <nav className="hidden items-center gap-5 xl:flex">
          {NAV_KEYS.map(([to, key]) => (
            <NavLink
              key={to}
              to={to}
              data-testid={`nav-${to.slice(1)}`}
              className={({ isActive }) =>
                `dt-link-underline whitespace-nowrap text-[12px] font-medium transition-colors ${
                  isActive ? "text-black" : "text-zinc-500 hover:text-black"
                }`
              }
            >
              {t(key)}
            </NavLink>
          ))}
        </nav>
        <div className="hidden shrink-0 items-center gap-3 lg:flex">
          <LocaleSwitch />
          {user ? (
            <>
              <Link to={homeFor(user)} data-testid="nav-portal" className="whitespace-nowrap text-[12px] font-medium text-zinc-600 hover:text-black">
                {t("nav.portal")}
              </Link>
              <button data-testid="nav-logout" onClick={logout} className="whitespace-nowrap text-[12px] font-medium text-zinc-400 hover:text-black">
                {t("nav.logout")}
              </button>
            </>
          ) : (
            <Link to="/login" data-testid="nav-login" className="text-[13px] font-medium text-zinc-600 hover:text-black">
              {t("nav.login")}
            </Link>
          )}
          {pathname !== primaryPath && (
            <Link
              to={primaryPath}
              data-testid={user ? "header-order-btn" : "header-free-diagnostic-btn"}
              className="group inline-flex shrink-0 items-center gap-2 whitespace-nowrap rounded-full bg-black px-5 py-2.5 text-[12px] font-semibold text-white transition-colors hover:bg-zinc-700"
            >
              {primaryLabel}
              <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          )}
        </div>
        <div className="flex items-center gap-4 lg:hidden">
          <LocaleSwitch compact />
          <button data-testid="mobile-menu-toggle" onClick={() => setOpen((v) => !v)} aria-label="Menu">
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>
      {open && (
        <div className="max-h-[80vh] overflow-y-auto border-t border-black/10 bg-white px-5 pb-8 pt-4 lg:hidden" data-testid="mobile-menu">
          <div className="flex flex-col">
            {[...NAV_KEYS, ["/for-professionals", "nav.pros"], ["/for-laboratories", "nav.labs"], ["/about", "nav.about"], ["/contact", "nav.contact"]].map(
              ([to, key]) => (
                <Link
                  key={to}
                  to={to}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-${to.slice(1)}`}
                  className="border-b border-black/5 py-3 font-display text-2xl"
                >
                  {t(key)}
                </Link>
              )
            )}
            <Link
              to={user ? homeFor(user) : "/login"}
              onClick={() => setOpen(false)}
              data-testid="mobile-nav-portal"
              className="border-b border-black/5 py-3 font-display text-2xl"
            >
              {user ? t("nav.portal") : t("nav.login")}
            </Link>
          </div>
          <Link
            to={primaryPath}
            onClick={() => setOpen(false)}
            data-testid={user ? "mobile-order-btn" : "mobile-free-diagnostic-btn"}
            className="mt-6 block rounded-full bg-black px-6 py-3.5 text-center text-sm font-semibold text-white"
          >
            {primaryLabel}
          </Link>
        </div>
      )}
    </header>
  );
};

const Footer = () => {
  const { t } = useI18n();
  const cols = [
    [t("common.experience"), [["/how-it-works", "nav.how"], ["/analysis", "nav.analysis"], ["/kit", "nav.kit"], ["/hair-health-score", "nav.score"], ["/pricing", "nav.pricing"]]],
    [t("nav.science"), [["/science", "nav.science"], ["/laboratory-partners", "nav.partners"], ["/for-laboratories", "nav.labs"], ["/for-professionals", "nav.pros"]]],
    ["DOCTHAIR", [["/about", "nav.about"], ["/faq", "nav.faq"], ["/contact", "nav.contact"], ["/legal/privacy", "nav.privacy"], ["/legal/terms", "nav.terms"], ["/legal/cookies", "nav.cookies"]]],
  ];
  return (
    <footer className="border-t border-black/10 bg-[#0A0A0A] text-white">
      <div className="mx-auto max-w-[1400px] px-5 py-16 md:px-10 md:py-24">
        <div className="grid gap-12 md:grid-cols-12">
          <div className="md:col-span-4">
            <Logo dark />
            <p className="mt-6 max-w-sm text-sm leading-relaxed text-zinc-400">{t("hero.sub")}</p>
            <Link
              to="/order"
              data-testid="footer-order-btn"
              className="mt-8 inline-flex items-center gap-2 rounded-full border border-white/25 px-5 py-2.5 text-[13px] font-semibold transition-colors hover:bg-white hover:text-black"
            >
              {t("cta.analyse")}
            </Link>
          </div>
          {cols.map(([title, links]) => (
            <div key={title} className="md:col-span-2">
              <p className="overline !text-zinc-500">{title}</p>
              <div className="mt-5 flex flex-col gap-3 text-sm text-zinc-300">
                {links.map(([to, key]) => (
                  <Link key={to} to={to} className="hover:text-white">{t(key)}</Link>
                ))}
              </div>
            </div>
          ))}
          <div className="md:col-span-2">
            <p className="overline !text-zinc-500">{t("common.assurances")}</p>
            <ul className="mt-5 flex flex-col gap-3 text-sm text-zinc-300">
              <li>{t("footer.a1")}</li>
              <li>{t("footer.a2")}</li>
              <li>{t("footer.a3")}</li>
              <li>{t("footer.a4")}</li>
            </ul>
          </div>
        </div>
        <div className="mt-16 flex flex-col gap-3 border-t border-white/10 pt-8 text-xs text-zinc-500 md:flex-row md:justify-between">
          <p className="font-mono-dt">© {new Date().getFullYear()} DOCTHAIR — Paris</p>
          <p className="max-w-2xl">{t("legal.disclaimer")}</p>
        </div>
      </div>
    </footer>
  );
};

export const Layout = ({ children }) => (
  <div className="flex min-h-screen flex-col bg-white">
    <Header />
    <main className="flex-1">{children}</main>
    <Footer />
  </div>
);

export const AppShell = ({ title, nav, active, onNavigate, children, testId = "app-shell" }) => {
  const { user, logout } = useAuth();
  const { t } = useI18n();
  return (
    <div className="min-h-screen bg-[#F8F9FA]" data-testid={testId}>
      <header className="sticky top-0 z-40 border-b border-black/10 bg-white/85 backdrop-blur-xl">
        <div className="mx-auto flex h-[68px] max-w-[1500px] items-center justify-between gap-4 px-5 md:px-8">
          <div className="flex items-baseline gap-4">
            <Logo />
            <span className="hidden font-mono-dt text-[10px] text-zinc-400 sm:inline">{title}</span>
          </div>
          <div className="flex items-center gap-4">
            <LocaleSwitch compact />
            <span className="hidden text-xs text-zinc-500 sm:inline" data-testid="shell-user">
              {user?.email}
            </span>
            <button
              onClick={logout}
              data-testid="shell-logout-btn"
              className="rounded-full border border-black/20 px-4 py-2 text-[12px] font-semibold transition-colors hover:bg-zinc-50"
            >
              {t("nav.logout")}
            </button>
          </div>
        </div>
        {nav ? (
          <div className="mx-auto max-w-[1500px] overflow-x-auto px-5 md:px-8">
            <div className="flex gap-6 border-t border-black/5 py-3">
              {nav.map((n) => (
                <button
                  key={n.key}
                  data-testid={`shell-tab-${n.key}`}
                  onClick={() => onNavigate(n.key)}
                  className={`whitespace-nowrap text-[13px] font-medium transition-colors ${
                    active === n.key ? "text-black underline underline-offset-8" : "text-zinc-500 hover:text-black"
                  }`}
                >
                  {n.label}
                </button>
              ))}
            </div>
          </div>
        ) : null}
      </header>
      <main className="mx-auto max-w-[1500px] px-5 py-8 md:px-8 md:py-10">{children}</main>
    </div>
  );
};

export default Layout;
